/**
 * Marketing i18n fragment — intgAfas (integrations/afas).
 *
 * Source of truth for every claim on this page: content/docs/{en,nl}/admin/
 * hr-governance.md § "Connecting AFAS Profit and Visma Raet Youforce" and the
 * shipped code in src/server/hr/afas-connector.ts + infra-credentials.ts.
 * Only shipped behaviour is described: read-only GetConnector views with
 * prescribed field ids, one-person filtering with explicit projection,
 * per-organization credentials with all-or-nothing resolution, the single
 * `connection` health capability (a filtered read that can match no employee),
 * honest failure with reason codes, and the governed answer path on top.
 * Deliberately NOT claimed: any write-back, salary amounts or payslips, a
 * self-service connection form, per-read health lanes, or a livetest against a
 * real AFAS Profit environment.
 */

export const intgAfasEn = {
  meta: {
    title: "AFAS Profit integration — read-only HR data behind an approval model",
    description:
      "FixControl reads a named set of fields from your own AFAS Profit GetConnector views — remaining leave hours with an as-of date, a payroll explanation code — filters every call on one person, and writes nothing back. An answer about someone's own situation is a draft until a second person approves it.",
  },
  hero: {
    eyebrow: "AFAS Profit integration",
    title: "AFAS data read-only,",
    highlight: "answers only after approval",
    subtitle:
      "An employee of your organization asks about their leave balance or a line on their payslip. FixControl reads the named fields it needs from your own AFAS Profit views, filtered on that one person, and writes nothing back. The answer about their own situation is a draft until someone on your team approves it.",
    primaryCta: "Book a demo",
    secondaryCta: "See the HR permission model",
    bullet1: "Read-only — nothing is written back",
    bullet2: "Named fields, not a personnel file",
    bullet3: "Credentials per organization",
  },
  what: {
    eyebrow: "What it does",
    title: "What the AFAS Profit connection covers",
  },
  features: {
    readOnly: {
      title: "Read-only by construction",
      body: "The connection has read methods and nothing else. No leave request, no profile change, no record created in AFAS — FixControl reads what an answer needs and leaves your HR system exactly as it was.",
    },
    namedViews: {
      title: "Your own GetConnector views",
      body: "FixControl talks to AFAS Profit through three GetConnector views you define yourself, with prescribed field ids: employee, leave and payroll. If your system names fields differently, you map them in your own view — FixControl never guesses a field.",
    },
    projection: {
      title: "One person per call, projected onto named fields",
      body: "Every call filters on a single person and takes at most one row, then projects the response onto the fixed shape those field ids describe. Widening a view on your side cannot leak extra personal data into an answer.",
    },
    credentials: {
      title: "Credentials per organization",
      body: "An app-connector token — encrypted at rest — plus your ProfitRestServices URL and the three view names. A half-filled entry is ignored rather than merged with platform defaults, so one environment's token can never be paired with another environment's host.",
    },
    health: {
      title: "A connection check, before an employee runs into it",
      body: "A periodic check reads the employee view with a filter value that can match no person. A revoked token or a rotated secret surfaces as a failing check, an audit event and an administrator notification — not as a failed answer to an employee.",
    },
    governedAnswer: {
      title: "The data is read; the answer still needs a person",
      body: "What comes back from AFAS does not become an outgoing answer on its own. A question about someone's own situation becomes a draft or a review case, bound to a named reviewer, and a second person approves before it is delivered.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "What the AFAS connection can and cannot do",
    description:
      "The connection has one health capability on purpose: the connection itself. A single missing employee record is a data condition, not a broken integration, so it routes that one question to a person instead of colouring the connection red for everyone.",
    leaveBalance: {
      name: "Remaining leave hours",
      body: "Read from the leave view you define, with the as-of date that belongs to the value. Available where that view exists and exposes the prescribed field ids.",
    },
    payrollCode: {
      name: "Payroll explanation code",
      body: "An explanation code with an as-of date — never an amount, never a payslip document. Reading it also requires the separate payroll permission on the reader's project role.",
    },
    projection: {
      name: "Field projection on every call",
      body: "Filtering on one person and projecting onto the prescribed fields is not configurable — the raw row never leaves the connector, so it cannot end up in an answer or in a prompt.",
    },
    connectionCheck: {
      name: "Connection check",
      body: "A filtered read that can match no employee proves the endpoint is reachable and the token is authorised for that view, without touching a real person's record.",
    },
    writeBack: {
      name: "Writing anything back to AFAS",
      body: "Not part of the connection, at any level. There is no create, update or delete path — no leave request, no absence registration, no profile change.",
    },
    salaryAmounts: {
      name: "Salary amounts & payslip documents",
      body: "Out of scope on purpose. The payroll view returns an explanation code and its as-of date; amounts and documents are not read, so they cannot appear in an answer or in an audit record.",
    },
  },
  governed: {
    eyebrow: "When AFAS cannot answer",
    title: "A missing value never becomes a made-up one",
    noteTitle: "Honest failure, with a reason on record",
    noteBody:
      "A missing record, a value that cannot be parsed, an unexpected response shape and an unreachable endpoint all lead to the same place: the question is handed to a person, and the reason is recorded. No number is invented, and a leave balance is never quietly defaulted to zero. Error messages carry a stable code and at most an HTTP status — never a token, a URL, a response body or an e-mail address. The model never sees any of this: it receives the question text to propose a classification, and personnel data, retrieved documents and answer bodies are not sent to a model at all.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Questions about the AFAS Profit integration",
    q1: {
      q: "Does FixControl write anything back to AFAS?",
      a: "No. The connection is read-only by construction: it has read methods and no create, update or delete path. An employee cannot request leave or change a record through FixControl, and no answer flow can either.",
    },
    q2: {
      q: "Which fields does FixControl read?",
      a: "The ones your three GetConnector views expose under prescribed field ids: employee id, e-mail, employment percentage, employment type and collective agreement; remaining leave hours with an as-of date; and a payroll explanation code with an as-of date. Never a salary amount, never a payslip document.",
    },
    q3: {
      q: "What happens if AFAS is unreachable or a value is missing?",
      a: "The question goes to a person and the reason is recorded. A missing record, an unparseable value or an unexpected response shape are treated the same way as an outage — the one thing that never happens is an invented number or a leave balance defaulted to zero.",
    },
    q4: {
      q: "Where do the credentials live?",
      a: "With your own organization. The app-connector token is encrypted at rest and stored against your organization's connection entry, together with your ProfitRestServices URL and view names. A half-filled entry is ignored rather than merged with platform defaults, so credentials from one environment can never be paired with another environment's host. Creating that entry — and naming the projects that may use it — is done together with us during onboarding; there is no self-service connection screen for AFAS yet.",
    },
    q5: {
      q: "Can a project start reading from AFAS on its own?",
      a: "No. Configuring the credentials only makes the connection available to your organization; a project has to list it as an active connection before anything is read, and answering has to be switched on for that project at all. An organization that configured nothing simply has no such connection.",
    },
    q6: {
      q: "Does an answer about my leave balance go out automatically?",
      a: "No. Anything touching an employee's own situation becomes a draft or a review case. The approval is bound to that exact answer text, the person who requested it cannot decide it, it expires after 72 hours on the server, and delivery consumes it so a double click cannot send twice.",
    },
  },
  related: {
    hrGovernance: {
      label: "HR questions with the right permissions",
      description: "The permission model, assignment and approval around the answer.",
    },
    youforce: {
      label: "Visma Raet Youforce",
      description: "The same read-only contract on a Youforce domain API.",
    },
    approvalLedger: {
      label: "AI approval ledger",
      description: "Who approved what, from which channel, on which grounds.",
    },
  },
  cta: {
    title: "See what an AFAS-backed answer has to pass",
    description:
      "Book a demo and follow one leave question from the AFAS read through assignment, approval and delivery.",
    primary: "Book a demo",
    secondary: "Talk to us",
  },
};

export const intgAfasNl = {
  meta: {
    title: "AFAS Profit-integratie — alleen-lezen HR-gegevens achter een goedkeuringsmodel",
    description:
      "FixControl leest een benoemde set velden uit je eigen AFAS Profit GetConnector-overzichten — resterende verlofuren met peildatum, een looncode — filtert elke aanroep op één persoon, en schrijft niets terug. Een antwoord over iemands eigen situatie blijft concept tot een tweede persoon het goedkeurt.",
  },
  hero: {
    eyebrow: "AFAS Profit-integratie",
    title: "AFAS-gegevens alleen-lezen,",
    highlight: "antwoorden pas na goedkeuring",
    subtitle:
      "Een medewerker van je organisatie vraagt naar het verlofsaldo of een regel op de loonstrook. FixControl leest de benoemde velden die daarvoor nodig zijn uit je eigen AFAS Profit-overzichten, gefilterd op die ene persoon, en schrijft niets terug. Het antwoord over de eigen situatie blijft concept tot iemand van je team het goedkeurt.",
    primaryCta: "Boek een demo",
    secondaryCta: "Bekijk het HR-rechtenmodel",
    bullet1: "Alleen-lezen — er wordt niets teruggeschreven",
    bullet2: "Benoemde velden, geen personeelsdossier",
    bullet3: "Inloggegevens per organisatie",
  },
  what: {
    eyebrow: "Wat het doet",
    title: "Wat de AFAS Profit-koppeling dekt",
  },
  features: {
    readOnly: {
      title: "Alleen-lezen, van bouw af aan",
      body: "De koppeling heeft leesmethoden en verder niets. Geen verlofaanvraag, geen profielwijziging, geen record dat in AFAS ontstaat — FixControl leest wat een antwoord nodig heeft en laat je HR-systeem precies zoals het was.",
    },
    namedViews: {
      title: "Je eigen GetConnector-overzichten",
      body: "FixControl praat met AFAS Profit via drie GetConnector-overzichten die je zelf inricht, met voorgeschreven veld-id's: medewerker, verlof en loon. Heten velden bij jou anders, dan map je ze in je eigen overzicht — FixControl gokt nooit een veld.",
    },
    projection: {
      title: "Eén persoon per aanroep, geprojecteerd op benoemde velden",
      body: "Elke aanroep filtert op één persoon en haalt hooguit één rij op, en projecteert het antwoord daarna op de vaste vorm die die veld-id's beschrijven. Een verbreed overzicht aan jouw kant kan zo geen extra persoonsgegevens in een antwoord laten lekken.",
    },
    credentials: {
      title: "Inloggegevens per organisatie",
      body: "Een app-connector-token — versleuteld opgeslagen — plus je ProfitRestServices-URL en de drie overzichtsnamen. Een half ingevulde invoer wordt genegeerd in plaats van samengevoegd met platformwaarden, zodat het token van de ene omgeving nooit aan de host van een andere omgeving raakt.",
    },
    health: {
      title: "Een verbindingscontrole, vóór een medewerker erop stuit",
      body: "Een periodieke controle leest het medewerkersoverzicht met een filterwaarde die op niemand kan matchen. Een ingetrokken token of geroteerd geheim komt zo boven als een falende controle, een auditgebeurtenis en een beheerdersmelding — niet als een mislukt antwoord aan een medewerker.",
    },
    governedAnswer: {
      title: "De gegevens worden gelezen; het antwoord vraagt nog steeds een mens",
      body: "Wat AFAS teruggeeft, wordt niet uit zichzelf een uitgaand antwoord. Een vraag over de eigen situatie wordt een concept of een reviewzaak, gebonden aan een benoemde behandelaar, en een tweede persoon keurt goed voordat het wordt afgeleverd.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "Wat de AFAS-koppeling wel en niet kan",
    description:
      "De koppeling heeft bewust één health-capability: de verbinding zelf. Eén ontbrekend medewerkerrecord is een datasituatie, geen kapotte koppeling — die ene vraag gaat naar een mens in plaats van dat de koppeling voor iedereen rood kleurt.",
    leaveBalance: {
      name: "Resterende verlofuren",
      body: "Gelezen uit het verlofoverzicht dat je zelf inricht, met de peildatum die bij de waarde hoort. Beschikbaar waar dat overzicht bestaat en de voorgeschreven veld-id's toont.",
    },
    payrollCode: {
      name: "Looncode",
      body: "Een uitlegcode met peildatum — nooit een bedrag, nooit een loonstrookdocument. Om hem te lezen is bovendien het aparte loonrecht op de projectrol van de lezer nodig.",
    },
    projection: {
      name: "Veldprojectie bij elke aanroep",
      body: "Filteren op één persoon en projecteren op de voorgeschreven velden is niet instelbaar — de ruwe rij verlaat de koppeling niet, en kan dus niet in een antwoord of in een prompt belanden.",
    },
    connectionCheck: {
      name: "Verbindingscontrole",
      body: "Een gefilterde leesactie die op geen enkele medewerker kan matchen, bewijst dat het endpoint bereikbaar is en het token voor dat overzicht geautoriseerd — zonder het record van een echte persoon aan te raken.",
    },
    writeBack: {
      name: "Iets terugschrijven naar AFAS",
      body: "Geen onderdeel van de koppeling, op geen enkel niveau. Er is geen aanmaak-, wijzig- of verwijderpad — geen verlofaanvraag, geen verzuimregistratie, geen profielwijziging.",
    },
    salaryAmounts: {
      name: "Salarisbedragen & loonstrookdocumenten",
      body: "Bewust buiten scope. Het loonoverzicht geeft een uitlegcode met peildatum terug; bedragen en documenten worden niet gelezen, en kunnen dus niet in een antwoord of in een auditregel opduiken.",
    },
  },
  governed: {
    eyebrow: "Als AFAS geen antwoord kan geven",
    title: "Een ontbrekende waarde wordt nooit een verzonnen waarde",
    noteTitle: "Eerlijk falen, met de reden vastgelegd",
    noteBody:
      "Een ontbrekend record, een waarde die niet te lezen is, een onverwachte antwoordvorm en een onbereikbaar endpoint komen allemaal op dezelfde plek uit: de vraag gaat naar een mens, en de reden wordt vastgelegd. Er wordt geen getal verzonnen, en een verlofsaldo wordt nooit stilletjes op nul gezet. Foutmeldingen dragen een vaste code en hooguit een HTTP-status — nooit een token, een URL, een antwoordbody of een e-mailadres. Het model ziet dit alles niet: het krijgt de vraagtekst om een classificatie voor te stellen, en dossiergegevens, opgehaalde documenten en antwoordteksten gaan niet naar een model.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Vragen over de AFAS Profit-integratie",
    q1: {
      q: "Schrijft FixControl iets terug naar AFAS?",
      a: "Nee. De koppeling is van bouw af aan alleen-lezen: ze heeft leesmethoden en geen aanmaak-, wijzig- of verwijderpad. Een medewerker kan via FixControl geen verlof aanvragen of een record wijzigen, en geen enkele antwoordroute kan dat ook.",
    },
    q2: {
      q: "Welke velden leest FixControl?",
      a: "Die je drie GetConnector-overzichten tonen onder voorgeschreven veld-id's: medewerker-id, e-mail, dienstverbandpercentage, dienstverbandsoort en cao; resterende verlofuren met peildatum; en een looncode met peildatum. Nooit een salarisbedrag, nooit een loonstrookdocument.",
    },
    q3: {
      q: "Wat gebeurt er als AFAS onbereikbaar is of een waarde ontbreekt?",
      a: "De vraag gaat naar een mens en de reden wordt vastgelegd. Een ontbrekend record, een onleesbare waarde of een onverwachte antwoordvorm worden net zo behandeld als een storing — het enige dat nooit gebeurt, is een verzonnen getal of een verlofsaldo dat op nul wordt gezet.",
    },
    q4: {
      q: "Waar staan de inloggegevens?",
      a: "Bij je eigen organisatie. Het app-connector-token wordt versleuteld opgeslagen bij de koppelingsinvoer van je organisatie, samen met je ProfitRestServices-URL en de overzichtsnamen. Een half ingevulde invoer wordt genegeerd in plaats van samengevoegd met platformwaarden, zodat inloggegevens uit de ene omgeving nooit aan de host van een andere omgeving raken. Die invoer aanmaken — en benoemen welke projecten haar mogen gebruiken — doen we samen met je tijdens de onboarding; een zelfbedieningsscherm voor de AFAS-koppeling is er nog niet.",
    },
    q5: {
      q: "Kan een project uit zichzelf uit AFAS gaan lezen?",
      a: "Nee. Inloggegevens instellen maakt de koppeling alleen beschikbaar voor je organisatie; een project moet haar als actieve koppeling vermelden voordat er iets wordt gelezen, en beantwoording moet voor dat project überhaupt aanstaan. Een organisatie die niets instelde, heeft simpelweg geen zo'n koppeling.",
    },
    q6: {
      q: "Gaat een antwoord over mijn verlofsaldo automatisch de deur uit?",
      a: "Nee. Alles wat de eigen situatie van een medewerker raakt, wordt een concept of een reviewzaak. De goedkeuring is gebonden aan precies die antwoordtekst, degene die haar aanvroeg kan er niet zelf over beslissen, ze vervalt na 72 uur op de server, en het afleveren verbruikt haar zodat dubbelklikken niet twee keer verstuurt.",
    },
  },
  related: {
    hrGovernance: {
      label: "HR-vragen met de juiste rechten",
      description: "Het rechtenmodel, de toewijzing en de goedkeuring rond het antwoord.",
    },
    youforce: {
      label: "Visma Raet Youforce",
      description: "Hetzelfde alleen-lezen-contract op een Youforce-domein-API.",
    },
    approvalLedger: {
      label: "AI-goedkeuringsgrootboek",
      description: "Wie keurde wat goed, vanuit welk kanaal, op welke gronden.",
    },
  },
  cta: {
    title: "Zie wat een antwoord uit AFAS moet passeren",
    description:
      "Boek een demo en volg één verlofvraag van de AFAS-leesactie tot en met toewijzing, goedkeuring en aflevering.",
    primary: "Boek een demo",
    secondary: "Neem contact op",
  },
};
